import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Eye, Edit2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Write your content here...',
  className,
  minHeight = '120px'
}: MarkdownEditorProps) {
  const [isPreview, setIsPreview] = useState(false);

  return (
    <div className={cn("border rounded-md overflow-hidden", className)}>
      {/* Editor toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
        <div className="text-xs text-gray-500">
          {isPreview ? 'Preview Mode' : 'Supports Markdown'}
        </div>
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsPreview(!isPreview)}
            className="h-8 px-2"
          >
            {isPreview ? (
              <>
                <Edit2 className="h-4 w-4 mr-1" />
                <span className="text-xs">Edit</span>
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-1" />
                <span className="text-xs">Preview</span>
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Editor content */}
      <div className="p-3">
        {isPreview ? (
          <div className="prose prose-sm max-w-none">
            {value ? (
              <ReactMarkdown>{value}</ReactMarkdown>
            ) : (
              <p className="text-gray-400 italic">{placeholder}</p>
            )}
          </div>
        ) : (
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="border-0 shadow-none focus-visible:ring-0 p-0"
            style={{ minHeight }}
          />
        )}
      </div>
      
      {/* Markdown help */}
      {!isPreview && (
        <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-500">
          <span className="font-medium">Formatting:</span> **bold**, *italic*, [link](url), - list, # heading
        </div>
      )}
    </div>
  );
}
